/**
 * CpaPrivilegeWarning — Phase 10 Task 10.5
 *
 * Sticky, non-dismissible warning banner displayed above every free-text
 * textarea where an external (witness) CPA may author content into their
 * own workpaper. The CPA's read-surface is already filtered by Tasks 10.4
 * (events) and 10.6 (consensus). This banner closes the *write-side*
 * loophole: the CPA could otherwise reference attorney strategy in their
 * own notes and bridge the partition, waiving work-product privilege.
 *
 * Design constraints:
 *   - Non-dismissible: NO close button, NO collapse state, NO toast variant.
 *   - Sticky in its scroll container — persists when the textarea is
 *     focused and the user scrolls.
 *   - role="region" with aria-labelledby (NOT role="alert" — alerts are
 *     transient by AT convention; this banner is persistent context).
 *   - Single source of truth for the copy via CPA_PRIVILEGE_WARNING_COPY
 *     so the e2e spec, manifest contract test, and runtime DOM all key
 *     off the same string.
 */

import React from 'react';

/**
 * Canonical warning copy. Exact wording dictated by the Phase 10 plan
 * checklist §Task 10.5; do NOT paraphrase. Exported so tests can import
 * it instead of duplicating the substring.
 */
export const CPA_PRIVILEGE_WARNING_COPY =
  'This workpaper records calculation review only. Do not reference case ' +
  'strategy, settlement positioning, or attorney argumentation. ' +
  'Referencing strategy in this record may waive attorney work-product privilege.';

export const CPA_PRIVILEGE_WARNING_HEADING = 'Workpaper privilege notice';

export interface CpaPrivilegeWarningProps {
  /** Test id override (defaults to "cpa-privilege-warning"). */
  testId?: string;
}

export default function CpaPrivilegeWarning({
  testId = 'cpa-privilege-warning',
}: CpaPrivilegeWarningProps) {
  const headingId = `${testId}-heading`;
  return (
    <div
      role="region"
      aria-labelledby={headingId}
      data-testid={testId}
      data-cpa-privilege-warning="true"
      className="sticky top-0 z-10 mb-2 rounded-md border px-3 py-2 text-sm shadow-sm"
      style={{ borderColor: 'var(--color-needs-human-tint-bd)', background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }}
    >
      <p
        id={headingId}
        className="mb-1 text-xs font-semibold uppercase tracking-wide"
        style={{ color: 'var(--color-needs-human-text)' }}
      >
        {CPA_PRIVILEGE_WARNING_HEADING}
      </p>
      <p className="leading-snug">{CPA_PRIVILEGE_WARNING_COPY}</p>
    </div>
  );
}

export { CpaPrivilegeWarning };
